import { Checkbox as MuiCheckbox } from '@mui/material';
import React, { ChangeEvent, FC } from 'react';

import { UserBasicFragment } from '../../../generated/graphql';

import Avatar from '../../MUI/Avatar';

import * as Styled from './styled';

export type Props = {
  badgeColor?: string;
  isChecked?: boolean;
  label: string;
  onChange?: (checked: boolean) => void;
  user?: UserBasicFragment;
};

/**
 * A single item that represents checkbox with label and avatar (optionally)
 *
 * @param props            Props passed to the component
 * @param props.badgeColor Color of the badge
 * @param props.isChecked  Whether the checkbox is checked
 * @param props.label      Label of the checkbox
 * @param props.onChange   Callback to be called when the checkbox is toggled
 * @param props.user       User data to display avatar
 * @returns                The component itself
 */
const CheckboxListItem: FC<Props> = ({
  badgeColor,
  isChecked = false,
  label,
  onChange = null,
  user,
}) => {
  const isViewMode = onChange === null;

  /**
   * Handle change of the checkbox state
   *
   * @param _event  Event that triggered the change
   * @param checked Whether the checkbox is checked
   */
  const handleChange = (
    _event: ChangeEvent<HTMLInputElement>,
    checked: boolean,
  ) => {
    if (onChange !== null) {
      onChange(checked);
    }
  };

  return (
    <Styled.StyledLabel
      aria-checked={isChecked}
      control={
        isViewMode ? (
          <span />
        ) : (
          <MuiCheckbox checked={isChecked} onChange={handleChange} />
        )
      }
      label={
        <>
          {badgeColor !== undefined && (
            <Styled.StyledBadge
              aria-label={`${label}-${badgeColor}`}
              data-bg-color={badgeColor}
            />
          )}
          {user !== undefined && <Avatar size="small" user={user} />}
          <Styled.StyledName>{label}</Styled.StyledName>
        </>
      }
    />
  );
};

export default CheckboxListItem;
